import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { MediaSlots } from '../components/MediaSlots';
import { Async, Badge, Card, Empty, Field, NumberField, TextField } from '../components/ui';
import { DeleteButton } from './Exercises';
import {
    CRITERION_KINDS, appendChild, childrenOf, describe, emptyCriterion,
    hasUnsetReferences, kindOf, performanceDependencies, removeAt, replaceAt,
    type CriterionKind,
} from '../lib/criterion';
import {
    DIMENSION_LABELS, MEASUREMENT_DIMENSIONS,
    type Criterion, type Exercise, type MeasurementDimension, type Session, type Skill,
} from '../lib/types';

export function Skills() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => ({
        skills: await data.listSkills(moduleId!),
        exercises: await data.listExercises(moduleId!),
        sessions: await data.listSessions(moduleId!),
    }), [moduleId]);
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);

    return (
        <Async state={state}>
            {({ skills, exercises, sessions }) => {
                if (editingId) {
                    const skill = editingId === 'new' ? null : skills.find((s) => s.id === editingId) ?? null;
                    return (
                        <SkillEditor
                            key={editingId}
                            moduleId={moduleId!}
                            skill={skill}
                            allSkills={skills}
                            exercises={exercises}
                            sessions={sessions}
                            onDone={() => { setEditingId(null); state.reload(); }}
                            onCancel={() => setEditingId(null)}
                        />
                    );
                }

                // The trap from spec §8.3: a performance rule is only satisfiable if some
                // prescription of that exercise records. Surface it while editing rather
                // than only at publish.
                const recorded = new Set(
                    sessions.flatMap((s) => s.blocks.flatMap((b) =>
                        b.exercises.filter((p) => 'recorded' in p.completion).map((p) => p.exerciseID))),
                );

                return (
                    <>
                        <div className="page-head">
                            <div>
                                <h1>Skill tree</h1>
                                <p className="muted">
                                    Optional per module. Nodes unlock by prerequisite and by rule.
                                </p>
                            </div>
                            <button className="btn" onClick={() => setEditingId('new')}>New skill</button>
                        </div>

                        {skills.length === 0 ? (
                            <Empty title="No skill tree"
                                hint="Modules without one simply show three entry points instead of four." />
                        ) : (
                            <div className="list">
                                {skills.map((skill) => {
                                    const unsatisfiable = performanceDependencies(skill.criterion)
                                        .filter((id) => !id || !recorded.has(id));
                                    return (
                                        <div key={skill.id} className="list-row">
                                            <Badge>Tier {skill.tier + 1}</Badge>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="title">{skill.name}</div>
                                                <div className="small muted">
                                                    {describe(
                                                        skill.criterion,
                                                        (id) => exercises.find((e) => e.id === id)?.name ?? 'an exercise',
                                                        (id) => sessions.find((s) => s.id === id)?.title ?? 'a session',
                                                    )}
                                                </div>
                                            </div>
                                            {skill.prerequisites.length > 0 && (
                                                <Badge tone="teal">{skill.prerequisites.length} prereq</Badge>
                                            )}
                                            {unsatisfiable.length > 0 && (
                                                <Badge tone="error">unsatisfiable</Badge>
                                            )}
                                            <button className="btn secondary small"
                                                onClick={() => setEditingId(skill.id)}>Edit</button>
                                            <DeleteButton label={skill.name}
                                                onDelete={() => data.deleteSkill(skill.id)}
                                                onDone={state.reload} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                );
            }}
        </Async>
    );
}

function SkillEditor({ moduleId, skill, allSkills, exercises, sessions, onDone, onCancel }: {
    moduleId: string; skill: Skill | null; allSkills: Skill[];
    exercises: Exercise[]; sessions: Session[];
    onDone: () => void; onCancel: () => void;
}) {
    const { data } = useApp();
    const [form, setForm] = useState<Omit<Skill, 'id' | 'module_id'>>({
        slug: skill?.slug ?? '',
        name: skill?.name ?? '',
        summary: skill?.summary ?? '',
        tags: skill?.tags ?? [],
        tier: skill?.tier ?? 0,
        criterion: skill?.criterion ?? { always: {} },
        exercise_id: skill?.exercise_id ?? null,
        prerequisites: skill?.prerequisites ?? [],
        image_media_id: skill?.image_media_id ?? null,
        demo_media_id: skill?.demo_media_id ?? null,
    });
    const [error, setError] = useState<string | null>(null);

    const recorded = new Set(
        sessions.flatMap((s) => s.blocks.flatMap((b) =>
            b.exercises.filter((p) => 'recorded' in p.completion).map((p) => p.exerciseID))),
    );
    const unsatisfiable = performanceDependencies(form.criterion).filter((id) => !id || !recorded.has(id));

    async function save() {
        setError(null);
        try {
            await data.upsertSkill({
                ...(skill ? { id: skill.id } : {}),
                module_id: moduleId,
                ...form,
                slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            });
            onDone();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }

    return (
        <>
            <div className="page-head">
                <div>
                    <div className="eyebrow">Skill</div>
                    <h1>{form.name || 'New skill'}</h1>
                </div>
                <div className="row">
                    <button className="btn secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn" disabled={!form.name} onClick={save}>Save skill</button>
                </div>
            </div>

            {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}

            {unsatisfiable.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <div className="banner error">
                        <strong>This rule can never be satisfied</strong>
                        <p className="small" style={{ marginTop: 6 }}>
                            It reads recorded performance for an exercise that no prescription in this
                            module records. Either pick a different exercise, or tick “Record
                            performance” on a prescription that uses it. Publishing will refuse until
                            then.
                        </p>
                    </div>
                </div>
            )}

            <Card>
                <div className="stack">
                    <div className="field-row">
                        <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <NumberField label="Tier (0 = foundations)" value={form.tier}
                            onChange={(v) => setForm({ ...form, tier: Math.max(0, v) })} />
                    </div>
                    <TextField label="Summary" value={form.summary}
                        onChange={(v) => setForm({ ...form, summary: v })} />
                    <MediaSlots moduleId={moduleId}
                        imageId={form.image_media_id} videoId={form.demo_media_id}
                        onImage={(id) => setForm({ ...form, image_media_id: id })}
                        onVideo={(id) => setForm({ ...form, demo_media_id: id })} />

                    <Field label="Prerequisites">
                        {allSkills.filter((s) => s.id !== skill?.id).length === 0 ? (
                            <p className="small muted">No other skills to depend on yet.</p>
                        ) : (
                            <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                                {allSkills.filter((s) => s.id !== skill?.id).map((other) => {
                                    const active = form.prerequisites.includes(other.id);
                                    return (
                                        <button
                                            key={other.id} type="button"
                                            className={`btn small ${active ? '' : 'secondary'}`}
                                            onClick={() => setForm({
                                                ...form,
                                                prerequisites: active
                                                    ? form.prerequisites.filter((p) => p !== other.id)
                                                    : [...form.prerequisites, other.id],
                                            })}
                                        >{other.name}</button>
                                    );
                                })}
                            </div>
                        )}
                    </Field>
                </div>
            </Card>

            <div style={{ marginTop: 20 }}>
                <Card>
                    <h2>Unlock rule</h2>
                    <p className="small muted" style={{ marginTop: 4, marginBottom: 12 }}>
                        {describe(
                            form.criterion,
                            (id) => exercises.find((e) => e.id === id)?.name ?? '(pick an exercise)',
                            (id) => sessions.find((s) => s.id === id)?.title ?? '(pick a session)',
                        )}
                    </p>
                    <CriterionNode
                        criterion={form.criterion}
                        path={[]}
                        exercises={exercises}
                        sessions={sessions}
                        recorded={recorded}
                        onChange={(path, next) =>
                            setForm({ ...form, criterion: replaceAt(form.criterion, path, next) })}
                        onRemove={(path) =>
                            setForm({ ...form, criterion: removeAt(form.criterion, path) })}
                        onAppend={(path, child) =>
                            setForm({ ...form, criterion: appendChild(form.criterion, path, child) })}
                    />
                </Card>
            </div>
        </>
    );
}

/**
 * Recursive editor for the Criterion tree.
 *
 * Renders itself for children, which is the only sane way to edit a recursive structure —
 * and the reason `criterion.ts` keeps all the tree surgery pure and separately tested.
 */
function CriterionNode({ criterion, path, exercises, sessions, recorded, onChange, onRemove, onAppend }: {
    criterion: Criterion;
    path: number[];
    exercises: Exercise[];
    sessions: Session[];
    recorded: Set<string>;
    onChange: (path: number[], next: Criterion) => void;
    onRemove: (path: number[]) => void;
    onAppend: (path: number[], child: Criterion) => void;
}) {
    const kind = kindOf(criterion);
    const isCombinator = kind === 'allOf' || kind === 'anyOf' || kind === 'not';
    const children = childrenOf(criterion);

    return (
        <div className={`criterion${isCombinator ? ' combinator' : ''}`}>
            <div className="criterion-head">
                <select
                    style={{ maxWidth: 230 }}
                    value={kind}
                    onChange={(e) => onChange(path, emptyCriterion(
                        e.target.value as CriterionKind,
                        exercises[0]?.id,
                        sessions[0]?.id,
                    ))}
                >
                    {CRITERION_KINDS.map((k) => (
                        <option key={k.kind} value={k.kind}>{k.label}</option>
                    ))}
                </select>

                {'personalBest' in criterion && (
                    <ExerciseRule
                        exerciseId={criterion.personalBest._0}
                        dimension={criterion.personalBest._1}
                        amount={criterion.personalBest.atLeast}
                        exercises={exercises}
                        recorded={recorded}
                        onChange={(id, dimension, amount) =>
                            onChange(path, { personalBest: { _0: id, _1: dimension, atLeast: amount } })}
                    />
                )}

                {'totalVolume' in criterion && (
                    <ExerciseRule
                        exerciseId={criterion.totalVolume._0}
                        dimension={criterion.totalVolume._1}
                        amount={criterion.totalVolume.atLeast}
                        exercises={exercises}
                        recorded={recorded}
                        onChange={(id, dimension, amount) =>
                            onChange(path, { totalVolume: { _0: id, _1: dimension, atLeast: amount, within: {} } })}
                    />
                )}

                {'completedSession' in criterion && (
                    <select
                        style={{ maxWidth: 240 }}
                        value={criterion.completedSession._0}
                        onChange={(e) => onChange(path, { completedSession: { _0: e.target.value } })}
                    >
                        <option value="">Choose a session…</option>
                        {sessions.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                    </select>
                )}

                {'sessionsCompleted' in criterion && (
                    <>
                        <input
                            type="number" className="input-narrow"
                            value={criterion.sessionsCompleted.atLeast}
                            onChange={(e) => onChange(path, {
                                sessionsCompleted: { atLeast: Number(e.target.value), program: null },
                            })}
                        />
                        <span className="small muted">sessions</span>
                    </>
                )}

                <div className="spacer" />

                {isCombinator && kind !== 'not' && (
                    <button className="btn secondary small"
                        onClick={() => onAppend(path, { always: {} })}>+ condition</button>
                )}

                {path.length > 0 && (
                    <button className="btn icon danger" onClick={() => onRemove(path)}
                        aria-label="Remove condition">×</button>
                )}
            </div>

            {hasUnsetReferences(criterion) && !isCombinator && (
                <p className="small" style={{ color: 'var(--error-800)', marginTop: 6 }}>
                    Nothing selected — this will block publishing.
                </p>
            )}

            {isCombinator && (
                <div className="criterion-children">
                    {children.map((child, index) => (
                        <CriterionNode
                            key={index}
                            criterion={child}
                            path={[...path, index]}
                            exercises={exercises}
                            sessions={sessions}
                            recorded={recorded}
                            onChange={onChange}
                            onRemove={onRemove}
                            onAppend={onAppend}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ExerciseRule({ exerciseId, dimension, amount, exercises, recorded, onChange }: {
    exerciseId: string; dimension: MeasurementDimension; amount: number;
    exercises: Exercise[]; recorded: Set<string>;
    onChange: (id: string, dimension: MeasurementDimension, amount: number) => void;
}) {
    const exercise = exercises.find((e) => e.id === exerciseId);
    const dimensions = exercise?.measured_in ?? MEASUREMENT_DIMENSIONS.slice();

    return (
        <>
            <select
                style={{ maxWidth: 200 }}
                value={exerciseId}
                onChange={(e) => onChange(e.target.value, dimension, amount)}
            >
                <option value="">Choose an exercise…</option>
                {exercises.map((e) => (
                    <option key={e.id} value={e.id}>
                        {e.name}{recorded.has(e.id) ? '' : ' (not recorded)'}
                    </option>
                ))}
            </select>
            <select
                style={{ maxWidth: 130 }}
                value={dimension}
                onChange={(e) => onChange(exerciseId, e.target.value as MeasurementDimension, amount)}
            >
                {dimensions.map((d) => <option key={d} value={d}>{DIMENSION_LABELS[d]}</option>)}
            </select>
            <span className="small muted">≥</span>
            <input
                type="number" className="input-narrow" value={amount}
                onChange={(e) => onChange(exerciseId, dimension, Number(e.target.value))}
            />
        </>
    );
}
